import { inject, Injectable, OnDestroy, signal, WritableSignal } from '@angular/core';
import { canCheckDeposit, Deposit, Deposits, isDepositExpired } from './deposits';
import { AnalyticsService } from '../analytics/analytics.service';
import { interval, NEVER, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { HttpService } from '../http/http.service';
import { JobService } from '../job/job.service';
import { Api } from '../../api/api';
import { TournamentService } from '../tournament/tournament.service';
import { EventName } from '../analytics/event-names';
import { JitApi } from '../../api/jit/jit-api';
import { DepositStatusData } from '../../api/jit/schemas/deposit-status';
import { WagerApi } from '../../api/wager/wager-api';
import { FindMatchData } from '../../api/wager/schemas/find-match';

@Injectable({
  providedIn: 'root',
})
export class DepositCheckerService implements OnDestroy {
  private analyticsService = inject(AnalyticsService);
  private httpService = inject(HttpService);
  private jobService = inject(JobService);
  private tournamentService = inject(TournamentService);

  private deposits: Deposits | undefined;
  private lastConsumedDepositSignal: WritableSignal<Deposit | undefined> = signal(undefined);
  private startStop$ = new Subject<boolean>();
  private destroy$ = new Subject<void>();

  readonly consumedDeposit$ = this.lastConsumedDepositSignal.asReadonly();

  initialise(_operatorId: string, _operatorPlatform: string | undefined): void {
    document.addEventListener('visibilitychange', this.onVisibilityChange.bind(this));

    this.startStop$
      .pipe(
        startWith(true),
        switchMap((running) => (running ? interval(2000).pipe(startWith(0)) : NEVER)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.checkDeposits();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setDeposits(deposits: Deposits) {
    this.stop();
    this.deposits = deposits;
    this.start();
  }

  clearDeposits() {
    this.stop();
    this.deposits = undefined;
  }

  start() {
    if (!this.deposits || this.deposits.getDeposits().length === 0) {
      return;
    }

    this.startStop$.next(true);
  }

  stop() {
    this.startStop$.next(false);
  }

  private async checkDeposits() {
    if (!this.deposits || this.deposits.getDeposits().length === 0) {
      this.stop();
      return;
    }

    const deposits = this.deposits.getDeposits();

    for (const deposit of deposits) {
      try {
        await this.processDeposit(deposit);
      } catch (error) {
        await this.analyticsService.handleError(error, `Process deposit: id: ${deposit.id}`);
      }
    }
  }

  private async processDeposit(deposit: Deposit) {
    if (!this.deposits) {
      this.analyticsService.errorWarning('DepositChecker: Deposits not set');
      return;
    }

    if (!canCheckDeposit(deposit)) {
      return;
    }

    const job = this.jobService.create<DepositStatusData>(async () => {
      return await JitApi.request(this.httpService, this.analyticsService).depositStatus(
        deposit.id,
      );
    });

    const depositStatusData = await job.runNoThrow();

    if (depositStatusData && depositStatusData.chargeStatus === 'PAID') {
      if (depositStatusData.depositStatus === 'AVAILABLE') {
        try {
          const job = deposit.tournamentId
            ? this.tournamentService.findTournamentMatch(
                deposit.gameId,
                deposit.tierId,
                deposit.id,
                deposit.tournamentId,
              )
            : this.jobService.create<FindMatchData>(async () => {
                return await WagerApi.request(this.httpService, this.analyticsService).findMatch(
                  deposit.gameId,
                  deposit.tierId,
                  deposit.id,
                );
              });

          const match = await job.run();
          if (match) {
            await this.stopChecking(deposit);
            deposit.roomId = match.roomId;
            this.lastConsumedDepositSignal.set(deposit);
          }
        } catch (error) {
          if (Api.isHttpErrorResponse(error)) {
            if (error.status === 404 || error.status === 500) {
              await this.stopChecking(deposit);
            }
          }
        }
      } else if (depositStatusData.depositStatus === 'AUTOREFUND') {
        await this.stopChecking(deposit);
      }
    } else if (isDepositExpired(deposit)) {
      await this.stopChecking(deposit);
    } else {
      this.deposits?.checked(deposit);
    }
  }

  private onVisibilityChange() {
    this.analyticsService.log(EventName.VisibilityChange, { state: document.hidden });

    this.stop(); // Ensure clean state before restarting

    if (!document.hidden) {
      // App is back in focus
      this.start(); // Restart deposit checking immediately
    }
  }

  private async stopChecking(deposit: Deposit) {
    if (!this.deposits) {
      return;
    }
    await this.deposits.remove(deposit);
    if (this.deposits.getDeposits().length === 0) {
      this.stop();
    }
  }
}
